import React, { useState, useCallback, useEffect } from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import { FileText, Trash, Upload, X } from "react-bootstrap-icons";
import { useDropzone } from "react-dropzone";
import { Controller } from "react-hook-form";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  setExcelData,
  setIsExcelValid,
  setValidationErrors,
} from "../config/PriceSlice";

const REQUIRED_FIELDS = [
  "Provider Service Code",
  "Provider Service Description",
  "Nphies Code",
  "Price",
  "Discount",
  "VAT Indicator ",
];

const ExcelPriceUpload = ({
  control,
  watch,
  errors,
  setValue,
  name = "excelFile",
}) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const validateHeaders = (rawHeaders) =>
    REQUIRED_FIELDS.every((field) => rawHeaders.includes(field));

  const validateFirstRow = (row) =>
    REQUIRED_FIELDS.every((field) => row[field]?.toString().trim() !== "");

  const validateExcelData = (jsonData) => {
    const errors = [];

    jsonData.forEach((row, index) => {
      const rowNum = index + 2;
      const rowErrors = [];

      // Required fields validation
      REQUIRED_FIELDS.forEach((field) => {
        if (!row[field]?.toString().trim()) {
          rowErrors.push({ row: rowNum, message: `${field} is required` });
        }
      });

      // Price validation (must be number >= 0)
      const price = parseFloat(row["Price"]);
      if (isNaN(price)) {
        rowErrors.push({
          row: rowNum,
          message: "Price must be a valid number",
        });
      } else if (price < 0) {
        rowErrors.push({
          row: rowNum,
          message: "Price must be greater than or equal to 0",
        });
      }

      // Discount validation (0-100)
      const discount = parseFloat(row["Discount"]);
      if (isNaN(discount)) {
        rowErrors.push({
          row: rowNum,
          message: "Discount must be a valid number",
        });
      } else if (discount < 0 || discount > 100) {
        rowErrors.push({
          row: rowNum,
          message: "Discount must be between 0 and 100",
        });
      }

      // VAT validation (must be 0 or 15)
      const vat = row["VAT Indicator "];
      if (vat !== 0 && vat !== 15) {
        rowErrors.push({
          row: rowNum,
          message: "VAT Indicator must be either 0 or 15",
        });

        const nphiesCode = row["Nphies Code"]?.toString().trim();
      }

      errors.push(...rowErrors);
    });

    return errors;
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const excelFile = acceptedFiles[0];
      if (!excelFile) return;

      setIsUploading(true);
      setFile(null);
      setValue(name, null);
      dispatch(setExcelData());
      dispatch(setValidationErrors());
      dispatch(setIsExcelValid());

      const reader = new FileReader();

      reader.onload = (e) => {
        let isValid = true;
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];

          // Get raw headers from row 1
          const rawHeaderRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const headers = rawHeaderRows[0] || [];

          if (!headers.length) {
            toast.error("Excel file is Empty");
            isValid = false;
            return;
          }

          // Validate headers explicitly
          if (!validateHeaders(headers)) {
            toast.error("Excel file is not valid");
            isValid = false;
            return;
          }

          // Now parse full data with header mapping
          const rawJson = XLSX.utils.sheet_to_json(sheet);

          const jsonData = rawJson.map((row, i) => ({
            id: `${Date.now()}-${i + 1}`,
            ...row,
          }));

          if (jsonData.length === 0) {
            toast.error("Excel file is Empty");
            isValid = false;
            return;
          }

          // Optional: validate first row content if needed
          if (!validateFirstRow(jsonData[0])) {
            toast.error("Row 2 has missing required fields.");
            isValid = false;
            return;
          }

          const errors = validateExcelData(jsonData);

          dispatch(setExcelData(jsonData));
          dispatch(setValidationErrors(errors));
          setFile(excelFile);
          setValue(name, excelFile);
          dispatch(setIsExcelValid(true));
        } catch (error) {
          console.error("Error reading Excel file:", error);
          toast.error("Error processing Excel file.");
          dispatch(
            setValidationErrors([{ row: 0, message: "Invalid file format." }])
          );
          isValid = false;
        } finally {
          setIsUploading(false);

          if (!isValid) {
            dispatch(setExcelData([]));
            dispatch(setValidationErrors([]));
            setFile(null);
            dispatch(setIsExcelValid(false));
          }
        }
      };

      reader.onerror = () => {
        toast.error("Error reading file");
        setIsUploading(false);
      };

      reader.readAsArrayBuffer(excelFile);
    },
    [setExcelData, setValidationErrors, setValue, name, setIsExcelValid]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
  });

  const handleRemove = () => {
    setFile(null);
    dispatch(setExcelData([]));
    dispatch(setValidationErrors([]));
    dispatch(setIsExcelValid(false));
    setValue(name, null);
  };
  useEffect(() => {
    if (watch("excelFile")) {
      setFile(watch("excelFile"));
    }
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: "Excel file is required" }}
      render={({ fieldState: { error } }) => (
        <Card className="mt-3">
          <Card.Body>
            <div {...getRootProps({ className: "dropzone-area py-4" })}>
              <input {...getInputProps()} />
              <div className="text-center">
                {isUploading ? (
                  <div className="py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0">Processing Excel file...</p>
                  </div>
                ) : (
                  <>
                    <div
                      className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center mx-auto mb-3"
                      style={{ width: 60, height: 60 }}
                    >
                      <Upload size={24} />
                    </div>
                    {isDragActive ? (
                      <p>Drop the Excel file here</p>
                    ) : (
                      <>
                        <h5 className="fw-bold">Upload Your Excel File</h5>
                        <p className="mb-3">
                          Drag and drop your .xlsx spreadsheet here
                        </p>
                        <Button variant="success">Choose File</Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {file && (
              <div className="mt-4">
                <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded d-flex align-items-center justify-content-center"
                      style={{ width: 40, height: 40 }}
                    >
                      <FileText className="text-primary" />
                    </div>
                    <div className="ms-3">
                      <h6 className="mb-0">{file.name}</h6>
                      <small className="text-muted">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRemove}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <div className="text-danger mt-2">
                {error.message || "Excel file is required"}
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    />
  );
};

export default ExcelPriceUpload;
